
import { Dropbox, DropboxAuth } from 'dropbox';

import { browser } from '../base/lib-meet-hour';

const DROPBOX_ERROR = {
    POPUP_BLOCKED: 'DROPBOX_POPUP_BLOCKED',
    POPUP_CLOSED: 'DROPBOX_POPUP_CLOSED',
    OAUTH_TIMEOUT: 'DROPBOX_OAUTH_TIMEOUT',
    MISSING_AUTH_CODE: 'DROPBOX_MISSING_AUTH_CODE',
    TOKEN_EXCHANGE_FAILED: 'DROPBOX_TOKEN_EXCHANGE_FAILED',
    REFRESH_EXCHANGE_FAILED: 'DROPBOX_REFRESH_EXCHANGE_FAILED',
    PROFILE_LOAD_FAILED: 'DROPBOX_PROFILE_LOAD_FAILED',
    SPACE_USAGE_LOAD_FAILED: 'DROPBOX_SPACE_USAGE_LOAD_FAILED'
};

/**
 * Builds Dropbox-specific error object with stable code and optional details.
 *
 * @param {string} code - Dropbox error code.
 * @param {string} details - Optional debug details.
 * @returns {Error}
 */
function createDropboxError(code: string, details?: string) {
    const error = new Error(code);

    error.code = code;

    if (details) {
        error.details = details;
    }

    return error;
}

/**
 * Checks whether the callback URL contains Dropbox OAuth code.
 *
 * @param {string} callbackUrl - URL received from oauth callback.
 * @returns {boolean}
 */
function hasDropboxAuthCode(callbackUrl: string) {
    if (typeof callbackUrl !== 'string' || !callbackUrl) {
        return false;
    }

    try {
        const parsedUrl = new URL(callbackUrl);

        return Boolean(parsedUrl.searchParams.get('code'));
    } catch (error) {
        return false;
    }
}

/**
 * Executes the oauth flow.
 *
 * @param {string} authUrl - The URL to oauth service.
 * @returns {Promise<string>} - The URL with the authorization details.
 */
function authorize(authUrl: string): Promise<string> {
    const windowName = `oauth${Date.now()}`;
    const isElectronApp = typeof browser !== 'undefined'
        && typeof browser.isElectron === 'function'
        && browser.isElectron();

    return new Promise((resolve, reject) => {

        let popup = null;
        let closeWatcher = null;
        let timeout = null;
        const cleanup = messageHandler => {
            window.removeEventListener('message', messageHandler);

            if (closeWatcher) {
                window.clearInterval(closeWatcher);
                closeWatcher = null;
            }

            if (timeout) {
                window.clearTimeout(timeout);
                timeout = null;
            }
        };

        const handleAuth = ({ data }) => {
            const hasMatchingWindowName = data?.windowName === windowName;
            const isElectronDeepLinkFallback = isElectronApp
                && data?.windowName === ''
                && hasDropboxAuthCode(data?.url);

            if (data?.type === 'dropbox-login' && (hasMatchingWindowName || isElectronDeepLinkFallback)) {
                if (popup) {
                    popup.close();
                }
                cleanup(handleAuth);
                resolve(data.url);
            }
        };

        window.addEventListener('message', handleAuth);
        popup = window.open(authUrl, windowName);

        if (!popup) {
            cleanup(handleAuth);
            reject(createDropboxError(DROPBOX_ERROR.POPUP_BLOCKED));

            return;
        }

        closeWatcher = window.setInterval(() => {
            if (popup.closed) {
                cleanup(handleAuth);
                reject(createDropboxError(DROPBOX_ERROR.POPUP_CLOSED));
            }
        }, 500);

        timeout = window.setTimeout(() => {
            cleanup(handleAuth);
            reject(createDropboxError(DROPBOX_ERROR.OAUTH_TIMEOUT));
        }, 180000);
    });
}

/**
 * Returns the token's expiry date as UNIX timestamp.
 *
 * @param {number} expiresIn - The seconds in which the token expires.
 * @returns {number} - The timestamp value for the expiry date.
 */
function getTokenExpiresAtTimestamp(expiresIn: number) {
    return new Date(Date.now() + (expiresIn * 1000)).getTime();
}

/**
 * Action to authorize the Jitsi Recording app in dropbox.
 *
 * @param {string} appKey - The Jitsi Recorder dropbox app key.
 * @param {string} redirectURI - The return URL.
 * @returns {Promise<Object>}
 */
export function _authorizeDropbox(
        appKey: string,
        redirectURI: string
): Promise<Object> {
    const dropbox = new DropboxAuth({ clientId: appKey });

    return dropbox.getAuthenticationUrl(redirectURI, undefined, 'code', 'offline', undefined, undefined, true)
        .then(authorize)
        .then(returnUrl => {
            const params = new URLSearchParams(new URL(returnUrl).search);
            const code = params.get('code');

            if (!code) {
                throw createDropboxError(DROPBOX_ERROR.MISSING_AUTH_CODE);
            }

            return dropbox.getAccessTokenFromCode(redirectURI, code);
        })
        .catch(error => {
            if (error?.code) {
                throw error;
            }

            throw createDropboxError(DROPBOX_ERROR.TOKEN_EXCHANGE_FAILED, error?.message);
        })
        .then(resp => {
            return {
                token: resp.result.access_token,
                rToken: resp.result.refresh_token,
                expireDate: getTokenExpiresAtTimestamp(resp.result.expires_in)
            };
        });
}


/**
 * Gets a new acccess token based on the refresh token.
 *
 * @param {string} appKey - The dropbox appKey.
 * @param {string} rToken - The refresh token.
 * @returns {Promise}
 */
export function getNewAccessToken(appKey: string, rToken: string) {
    const dropbox = new DropboxAuth({ clientId: appKey });

    dropbox.setRefreshToken(rToken);

    return dropbox.refreshAccessToken()
        .catch(error => {
            throw createDropboxError(DROPBOX_ERROR.REFRESH_EXCHANGE_FAILED, error?.message);
        })
        .then(() => {
            return {
                token: dropbox.getAccessToken(),
                rToken: dropbox.getRefreshToken(),
                expireDate: dropbox.getAccessTokenExpiresAt().getTime()
            };
        });
}

/**
 * Returns the display name for the current dropbox account.
 *
 * @param {string} token - The dropbox access token.
 * @param {string} appKey - The Jitsi Recorder dropbox app key.
 * @returns {Promise<string>}
 */
export function getDisplayName(token: string, appKey: string) {
    const dropboxAPI = new Dropbox({
        accessToken: token,
        clientId: appKey
    });

    return (
        dropboxAPI.usersGetCurrentAccount()
            .catch(error => {
                throw createDropboxError(DROPBOX_ERROR.PROFILE_LOAD_FAILED, error?.message);
            })
            .then(account => account.result.name.display_name));
}

/**
 * Returns information about the space usage for the current dropbox account.
 *
 * @param {string} token - The dropbox access token.
 * @param {string} appKey - The Jitsi Recorder dropbox app key.
 * @returns {Promise<Object>}
 */
export function getSpaceUsage(token: string, appKey: string) {
    const dropboxAPI = new Dropbox({
        accessToken: token,
        clientId: appKey
    });

    return dropboxAPI.usersGetSpaceUsage()
        .catch(error => {
            throw createDropboxError(DROPBOX_ERROR.SPACE_USAGE_LOAD_FAILED, error?.message);
        })
        .then(space => {
            const { allocation, used } = space.result;
            const { allocated } = allocation;

            return {
                allocated,
                used
            };
        });
}

/**
 * Returns <tt>true</tt> if the dropbox features is enabled and <tt>false</tt>
 * otherwise.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean}
 */
export function isEnabled(state: Object) {
    const { dropbox = {} } = state['features/base/config'];

    return typeof dropbox.appKey === 'string';
}
